/* ============================================================
   La Nuit — product-gallery.js
   <product-gallery> custom element.

   Renders a multi-image-per-variant product gallery. Reads a JSON
   variant->{f,g[]} map from a child <script type="application/json"
   data-variant-gallery>, listens for `variant:change` and toggles the
   `[hidden]` attribute on each <figure data-media-id data-variant-ids>.

   Also handles:
     - 180ms Web Animations fade-in on newly-shown figures (skipped if
       prefers-reduced-motion).
     - Thumb strip click → preview that media.
     - Thumb auto-scrollIntoView on selection (smooth, inline:center).
     - Eager-loading neighbors of the active variant for instant swaps.
     - gallery:preview / gallery:preview-clear hover events from
       sibling components (addon cards, complementary products).

   No external deps. Vanilla custom element. ESM-free.
   ============================================================ */

(function () {
  if (window.customElements && customElements.get('product-gallery')) return;

  const REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FADE_DURATION = REDUCED_MOTION ? 0 : 180;

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.thumbsList = this.querySelector('[data-gallery-thumbs]');
      this.figures = Array.from(this.querySelectorAll('.product-gallery__figure'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.sectionId = this.dataset.sectionId;
      this.variantMap = this._readVariantMap();
      this._previewMediaId = null;
      this._previewPrevState = null;

      this._bindThumbs();
      this._bindEvents();

      // If a variant is already selected on mount, sync.
      const initialVariant = this._currentVariantId();
      if (initialVariant) this._applyVariant(initialVariant, { animate: false });
    }

    /* ---------- private ---------- */

    _readVariantMap() {
      const tag = this.querySelector('script[data-variant-gallery]');
      if (!tag) return {};
      try {
        return JSON.parse(tag.textContent);
      } catch (e) {
        return {};
      }
    }

    _currentVariantId() {
      // Scope to the form's hidden id input. The variant-picker swatch
      // labels also carry [data-variant-id] (for hover-preview), so a
      // broad selector picks up a <label> first and .value is undefined,
      // which silently skips initial _applyVariant and leaves the gallery
      // stuck on Liquid's first figure.
      const section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
      const hidden = section && section.querySelector('input[data-variant-id][name="id"]');
      return hidden ? hidden.value : null;
    }

    _bindThumbs() {
      this.thumbs.forEach(btn => {
        btn.addEventListener('click', () => {
          const mediaId = btn.dataset.mediaId;
          if (!mediaId) return;
          this._setActiveByMediaId(mediaId);
        });
      });
    }

    _bindEvents() {
      // Variant change — dispatched by <variant-radios> (agent 1).
      document.addEventListener('variant:change', e => {
        const variantId = e.detail && (e.detail.variantId || e.detail.id || (e.detail.variant && e.detail.variant.id));
        if (!variantId) return;
        // Only react to events for our section, if scoped; otherwise apply globally.
        const evSection = e.detail && e.detail.sectionId;
        if (evSection && String(evSection) !== String(this.sectionId)) return;
        this._applyVariant(variantId, { animate: true });
      });

      // Hover-preview — temporary swap that reverts on clear.
      document.addEventListener('gallery:preview', e => {
        const mediaId = e.detail && e.detail.mediaId;
        const sectionScope = e.detail && e.detail.sectionId;
        if (sectionScope && String(sectionScope) !== String(this.sectionId)) return;
        if (!mediaId) return;
        // Stash the figure we'll restore on clear.
        if (!this._previewMediaId) {
          const current = this.figures.find(f => f.hasAttribute('aria-current'));
          this._previewPrevState = current ? current.dataset.mediaId : null;
        }
        this._previewMediaId = mediaId;
        this._setActiveByMediaId(mediaId, { keepOthersVisible: true });
      });

      document.addEventListener('gallery:preview-clear', e => {
        const sectionScope = e.detail && e.detail.sectionId;
        if (sectionScope && String(sectionScope) !== String(this.sectionId)) return;
        if (!this._previewMediaId) return;
        const restore = this._previewPrevState;
        this._previewMediaId = null;
        this._previewPrevState = null;
        if (restore) this._setActiveByMediaId(restore, { keepOthersVisible: true });
      });
    }

    _applyVariant(variantId, opts) {
      opts = opts || {};
      const entry = this.variantMap[variantId];
      // Fallback: variant unknown -> show all (default product.media behavior).
      const allIds = [];
      if (entry) {
        if (entry.f != null) allIds.push(entry.f);
        if (Array.isArray(entry.g)) entry.g.forEach(id => { if (allIds.indexOf(id) === -1) allIds.push(id); });
      }

      const idStr = String(variantId);
      const visibleFigures = [];
      this.figures.forEach(fig => {
        const owners = (fig.dataset.variantIds || '').split(',');
        const productLevel = owners.length === 1 && (owners[0] === '*' || owners[0] === '');
        const ownedByVariant = owners.indexOf(idStr) !== -1;
        const raw = fig.dataset.rawMediaId;
        const inMetafieldList = raw && allIds.map(String).indexOf(String(raw)) !== -1;

        let show;
        if (allIds.length > 0) {
          // Variant has specific images: show those + product-level images.
          show = inMetafieldList || productLevel || ownedByVariant;
        } else {
          // No featured_media and no gallery metafield for this variant:
          // fall back to showing every figure (default product.media).
          show = true;
        }

        if (show) {
          fig.hidden = false;
          visibleFigures.push(fig);
        } else {
          fig.hidden = true;
          fig.removeAttribute('aria-current');
        }
      });

      // Prefer the variant's featured_media as the "current" figure when
      // present — selecting a variant should surface its own image, not
      // keep the first product-level shot on stage. Falls back to the
      // first visible figure.
      if (visibleFigures.length > 0) {
        let active = null;
        if (entry && entry.f != null) {
          const featuredRaw = String(entry.f);
          active = visibleFigures.find(f => String(f.dataset.rawMediaId) === featuredRaw) || null;
        }
        if (!active) active = visibleFigures[0];
        this.figures.forEach(f => f.removeAttribute('aria-current'));
        active.setAttribute('aria-current', 'true');
        this._syncMedia();
        this._eagerLoadGroup(visibleFigures);
        if (opts.animate !== false) this._fadeIn(active);
        this._syncThumb(active.dataset.mediaId);
      }
    }

    /* Video housekeeping: only the current figure may play, and the zoom
       (lightbox) button only makes sense for a still image. */
    _syncMedia() {
      const cur = this.figures.find(f => f.hasAttribute('aria-current'));
      this.figures.forEach(f => {
        if (f === cur) return;
        f.querySelectorAll('video').forEach(v => { try { v.pause(); } catch (e) {} });
      });
      const zoom = this.querySelector('[data-gallery-zoom]');
      if (zoom) zoom.hidden = !!(cur && cur.dataset.mediaType && cur.dataset.mediaType !== 'image');
    }

    _setActiveByMediaId(mediaId, opts) {
      opts = opts || {};
      const target = this.figures.find(f => f.dataset.mediaId === mediaId);
      if (!target) return;
      if (target.hidden) target.hidden = false;
      this.figures.forEach(f => f.removeAttribute('aria-current'));
      target.setAttribute('aria-current', 'true');
      this._syncMedia();
      this._fadeIn(target);
      this._syncThumb(mediaId);
    }

    _fadeIn(el) {
      if (!el || FADE_DURATION === 0 || !el.animate) return;
      el.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: FADE_DURATION, easing: 'ease-out', fill: 'forwards' }
      );
    }

    _eagerLoadGroup(figures) {
      figures.forEach((fig, i) => {
        const img = fig.querySelector('img');
        if (!img) return;
        // First image is already active; preload next 2 neighbors eagerly.
        if (i <= 2 && img.getAttribute('loading') === 'lazy') {
          img.setAttribute('loading', 'eager');
        }
      });
    }

    _syncThumb(mediaId) {
      if (!this.thumbs.length) return;
      let activeThumb = null;
      this.thumbs.forEach(t => {
        const match = t.dataset.mediaId === mediaId;
        t.classList.toggle('is-active', match);
        t.setAttribute('aria-current', match ? 'true' : 'false');
        if (match) activeThumb = t;
      });
      if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
        try {
          activeThumb.scrollIntoView({
            behavior: REDUCED_MOTION ? 'auto' : 'smooth',
            inline: 'center',
            block: 'nearest'
          });
        } catch (e) {
          // Older browsers — fallback to no-arg scrollIntoView
          activeThumb.scrollIntoView();
        }
      }
    }

    /* ---------- public helpers (used by ProductLightbox) ---------- */

    getVisibleFigures() {
      return this.figures.filter(f => !f.hidden);
    }

    getCurrentMediaId() {
      const cur = this.figures.find(f => f.hasAttribute('aria-current'));
      return cur ? cur.dataset.mediaId : null;
    }

    openLightbox(mediaId) {
      if (this.dataset.lightbox === 'false') return;
      if (!window.LaNuitProductLightbox) return;
      window.LaNuitProductLightbox.openFor(this, mediaId || this.getCurrentMediaId());
    }
  }

  customElements.define('product-gallery', ProductGallery);

  /* ---------- ProductLightbox: fullscreen viewer (singleton) ----------
     Handles prev/next (buttons + keyboard + swipe), pinch-to-zoom,
     pan-when-zoomed, double-tap reset. Singleton because the modal is
     rendered once per PDP in sections/main-product.liquid. */

  const SWIPE_THRESHOLD = 50;
  const PINCH_MIN_SCALE = 1;
  const PINCH_MAX_SCALE = 4;

  class ProductLightbox {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-lightbox-stage]');
      this.counter = root.querySelector('[data-lightbox-counter]');
      this.prevBtn = root.querySelector('[data-lightbox-prev]');
      this.nextBtn = root.querySelector('[data-lightbox-next]');
      this.images = [];
      this.index = 0; this.gallery = null;
      // Gesture state
      this.scale = 1; this.translateX = 0; this.translateY = 0;
      this.pointers = new Map();
      this.pinchStartDist = 0; this.pinchStartScale = 1;
      this.lastTapTime = 0; this.swipeStartX = 0; this.swipeActive = false;

      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());
      document.addEventListener('keydown', (e) => {
        if (this.root.hasAttribute('hidden')) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      });
      const s = this.stage;
      if (s) {
        s.addEventListener('pointerdown', (e) => this._onPointerDown(e));
        s.addEventListener('pointermove', (e) => this._onPointerMove(e));
        s.addEventListener('pointerup', (e) => this._onPointerUp(e));
        s.addEventListener('pointercancel', (e) => this._onPointerUp(e));
      }
      this.root.addEventListener('modal:close', () => this._resetZoom());
    }

    openFor(gallery, mediaId) {
      this.gallery = gallery;
      const figures = gallery.getVisibleFigures();
      if (!figures.length) return;
      this.images = figures.map(fig => {
        const img = fig.querySelector('img');
        return img ? {
          mediaId: fig.dataset.mediaId,
          src: img.currentSrc || img.src,
          srcset: img.srcset || '',
          sizes: img.sizes || '',
          alt: img.alt || ''
        } : null;
      }).filter(Boolean);
      if (!this.images.length) return;
      this.index = 0;
      if (mediaId) {
        const i = this.images.findIndex(x => x.mediaId === mediaId);
        if (i !== -1) this.index = i;
      }
      this._render();
      if (typeof this.root.open === 'function') this.root.open();
      else { this.root.removeAttribute('hidden'); document.body.style.overflow = 'hidden'; }
    }

    next() { this._goTo(this.index + 1); }
    prev() { this._goTo(this.index - 1); }

    _goTo(i) {
      if (!this.images.length) return;
      const len = this.images.length;
      this.index = ((i % len) + len) % len;
      this._resetZoom();
      this._render({ animate: !REDUCED_MOTION });
      if (this.gallery) {
        const target = this.images[this.index];
        if (target && target.mediaId) this.gallery._setActiveByMediaId(target.mediaId);
      }
    }

    _render(opts) {
      if (!this.stage) return;
      opts = opts || {};
      const cur = this.images[this.index];
      if (!cur) return;
      let img = this.stage.querySelector('.product-lightbox__image');
      if (!img) {
        img = document.createElement('img');
        img.className = 'product-lightbox__image';
        img.draggable = false;
        this.stage.appendChild(img);
      }
      if (opts.animate) img.classList.remove('is-active');
      img.alt = cur.alt;
      if (cur.srcset) img.srcset = cur.srcset;
      if (cur.sizes) img.sizes = cur.sizes;
      img.src = cur.src;
      const activate = () => img.classList.add('is-active');
      if (opts.animate) requestAnimationFrame(() => requestAnimationFrame(activate));
      else activate();
      if (this.counter) this.counter.textContent = (this.index + 1) + ' / ' + this.images.length;
      const single = this.images.length <= 1;
      if (this.prevBtn) this.prevBtn.hidden = single;
      if (this.nextBtn) this.nextBtn.hidden = single;
    }

    /* ---------- gestures ---------- */

    _onPointerDown(e) {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.stage.setPointerCapture) {
        try { this.stage.setPointerCapture(e.pointerId); } catch (_) {}
      }
      const now = Date.now();
      if (now - this.lastTapTime < 300 && this.pointers.size === 1 && this.scale > 1) this._resetZoom();
      this.lastTapTime = now;
      if (this.pointers.size === 2) {
        const pts = Array.from(this.pointers.values());
        this.pinchStartDist = this._dist(pts[0], pts[1]);
        this.pinchStartScale = this.scale;
        this.swipeActive = false;
      } else if (this.pointers.size === 1 && this.scale === 1) {
        this.swipeStartX = e.clientX;
        this.swipeActive = true;
      }
    }

    _onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2) {
        const pts = Array.from(this.pointers.values());
        const dist = this._dist(pts[0], pts[1]);
        if (this.pinchStartDist > 0) {
          this.scale = Math.max(PINCH_MIN_SCALE, Math.min(PINCH_MAX_SCALE, this.pinchStartScale * (dist / this.pinchStartDist)));
          this._applyTransform();
        }
      } else if (this.pointers.size === 1 && this.scale > 1) {
        this.translateX += (e.movementX || 0);
        this.translateY += (e.movementY || 0);
        this._applyTransform();
      }
    }

    _onPointerUp(e) {
      this.pointers.delete(e.pointerId);
      if (this.swipeActive && this.scale === 1 && this.pointers.size === 0) {
        const dx = e.clientX - this.swipeStartX;
        if (Math.abs(dx) > SWIPE_THRESHOLD) { if (dx < 0) this.next(); else this.prev(); }
      }
      this.swipeActive = false;
      if (this.pointers.size < 2) { this.pinchStartDist = 0; this.pinchStartScale = this.scale; }
    }

    _dist(a, b) { const dx = a.x - b.x, dy = a.y - b.y; return Math.sqrt(dx * dx + dy * dy); }

    _applyTransform() {
      const img = this.stage.querySelector('.product-lightbox__image');
      if (!img) return;
      img.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      img.style.cursor = this.scale > 1 ? 'grab' : '';
      img.style.touchAction = this.scale > 1 ? 'none' : 'pan-y';
    }

    _resetZoom() {
      this.scale = 1; this.translateX = 0; this.translateY = 0;
      const img = this.stage && this.stage.querySelector('.product-lightbox__image');
      if (img) { img.style.transform = ''; img.style.cursor = ''; img.style.touchAction = ''; }
    }
  }

  /* ---------- bootstrap ---------- */

  function init() {
    const modal = document.getElementById('ProductLightbox');
    if (modal && !window.LaNuitProductLightbox) {
      window.LaNuitProductLightbox = new ProductLightbox(modal);
    }
    document.addEventListener('click', (e) => {
      const zoomBtn = e.target.closest('[data-gallery-zoom]');
      if (zoomBtn) {
        const g = zoomBtn.closest('product-gallery');
        if (g?.openLightbox) { e.preventDefault(); g.openLightbox(); }
        return;
      }
      const trigger = e.target.closest('[data-lightbox-trigger]');
      if (trigger) {
        const g = trigger.closest('product-gallery');
        if (g?.openLightbox) {
          e.preventDefault();
          const fig = trigger.closest('.product-gallery__figure');
          g.openLightbox(fig ? fig.dataset.mediaId : null);
        }
        return;
      }
      const img = e.target.closest('.product-gallery__figure:not([hidden]) .product-gallery__image');
      if (img) {
        const fig = img.closest('.product-gallery__figure');
        const g = img.closest('product-gallery');
        if (g?.openLightbox && fig) g.openLightbox(fig.dataset.mediaId);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
